import {detectOverflow, Middleware, Placement} from '@floating-ui/core';

const allowedPlacements: Placement[] = [
  'top-end',
  'top-start',
  'bottom-end',
  'bottom-start',
];

const allowedHorizontalPlacements: Placement[] = [
  'left-end',
  'left-start',
  'right-end',
  'right-start',
];

const autoPlacement: (horizontal: boolean) => Middleware = (horizontal) => ({
  name: 'dropdownAutoPlacement',
  async fn(middlewareArguments) {
    const {x, y, middlewareData, placement} = middlewareArguments;

    const placements = horizontal
      ? allowedHorizontalPlacements
      : allowedPlacements;

    const overflow = await detectOverflow(middlewareArguments);

    const currentIndex = middlewareData.dropdownAutoPlacement?.index ?? 0;
    const currentPlacement = placements[currentIndex];

    if (currentPlacement == null) {
      return {};
    }

    // Make `computeCoords` start from the right place
    if (placement !== currentPlacement) {
      return {
        x,
        y,
        reset: {
          placement: placements[0],
        },
      };
    }

    const allOverflows = [
      ...(middlewareData.dropdownAutoPlacement?.overflow ?? []),
      {
        placement: currentPlacement,
        overflow: {
          horizontal: Math.min(overflow.left, overflow.right),
          vertical: Math.min(overflow.top, overflow.bottom),
        },
      },
    ];

    const nextPlacement = placements[currentIndex + 1];

    // There are more placements to check
    if (nextPlacement) {
      return {
        data: {
          index: currentIndex + 1,
          overflow: allOverflows,
        },
        reset: {
          placement: nextPlacement,
        },
      };
    }

    const placementsSortedByLeastOverflow = allOverflows
      .slice()
      .sort((a, b) =>
        a.overflow.horizontal < b.overflow.horizontal
          ? 1
          : a.overflow.vertical < b.overflow.vertical
            ? 1
            : -1
      );
    const placementThatFitsOnAllSides =
      placementsSortedByLeastOverflow[0]?.placement;

    const resetPlacement =
      placementThatFitsOnAllSides ??
      placementsSortedByLeastOverflow[0].placement;

    if (resetPlacement !== placement) {
      return {
        data: {
          index: currentIndex + 1,
          overflow: allOverflows,
        },
        reset: {
          placement: resetPlacement,
        },
      };
    }

    return {};
  },
});

export default autoPlacement;
